/**
 * lazyWithReload.js
 * =================
 * Wrapper de React.lazy que se recupera automaticamente (uma única vez)
 * de chunks desatualizados após um novo deploy.
 *
 * Cenário: usuário mantém a aba aberta → novo deploy troca os hashes dos
 * chunks → main.js antigo pede um chunk que não existe mais → ChunkLoadError.
 * Solução: recarregar a página UMA vez para buscar index.html/main.js novos.
 *
 * Garantias:
 *  - reload apenas para erros de chunk (erros normais de JS propagam);
 *  - no máximo 1 recarga por janela de RELOAD_TTL_MS (sem loop);
 *  - flag removida após import bem-sucedido;
 *  - flag expirada (>60s) permite nova tentativa em deploy futuro.
 */
import React from 'react';

export const RELOAD_KEY = 'vukz_chunk_reload';
export const RELOAD_TTL_MS = 60 * 1000;

/**
 * Reconhece somente erros compatíveis com chunk desatualizado.
 * @param {Error} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  const name = String(error?.name || '');
  const message = String(error?.message || '');
  return (
    name === 'ChunkLoadError' ||
    name === 'CSS_CHUNK_LOAD_FAILED' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /CSS chunk load failed/i.test(message)
  );
}

/** Lê a flag; retorna timestamp (number) ou null se ausente/inválida. */
function readReloadFlag() {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function clearReloadFlag() {
  try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* noop */ }
}

/**
 * Telemetria não sensível de recuperação de chunk.
 * Sem token, e-mail, query string ou URL completa do erro.
 */
function trackChunkRecovery(error, reloadAttempted) {
  const payload = {
    event: 'chunk_load_recovery',
    route: window.location?.pathname || 'unknown',
    error_name: String(error?.name || 'Error'),
    reload_attempted: reloadAttempted,
  };
  try {
    const ai = window.appInsights;
    if (ai && typeof ai.trackEvent === 'function') {
      ai.trackEvent({ name: 'chunk_load_recovery', properties: payload });
    }
  } catch { /* telemetria nunca quebra a recuperação */ }
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('[lazyWithReload]', payload);
  }
}

/**
 * Substituto de React.lazy(() => import(...)) com auto-recuperação.
 * @param {() => Promise<{default: React.ComponentType}>} importFunction
 */
export function lazyWithReload(importFunction) {
  return React.lazy(async () => {
    try {
      const module = await importFunction();
      clearReloadFlag();
      return module;
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error; // erro real de JS continua visível
      }

      const previousTs = readReloadFlag();
      const recentReload =
        previousTs !== null && Date.now() - previousTs < RELOAD_TTL_MS;

      if (!recentReload) {
        try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* noop */ }
        trackChunkRecovery(error, true);
        window.location.reload();
        // Nunca resolve: a página vai recarregar; evita flash de erro.
        return new Promise(() => {});
      }

      // Já recarregamos há pouco e o chunk continua falhando → propaga
      // para o ErrorBoundary (sem loop). Flag limpa para tentativa futura.
      clearReloadFlag();
      trackChunkRecovery(error, false);
      throw error;
    }
  });
}

export default lazyWithReload;
